import * as amplitude from '@amplitude/analytics-browser';

let initialization: Promise<boolean> | undefined;

/** Initialize the dashboard's EU project once, before dispatching identity changes or events. */
export function initAnalytics(): Promise<boolean> {
    if (initialization) return initialization;

    // This is Amplitude's public ingestion key, injected by Create React App at build time.
    const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY?.trim();
    if (!apiKey) {
        console.warn('Dashboard analytics disabled: REACT_APP_AMPLITUDE_API_KEY is not configured.');
        initialization = Promise.resolve(false);
        return initialization;
    }

    try {
        initialization = amplitude.init(apiKey, {
            serverZone: 'EU',
            // Track explicit dashboard actions; automatic capture can include client data and auth URLs.
            autocapture: false,
        }).promise.then(() => true).catch((error) => {
            console.error('Error initializing dashboard analytics:', error);
            return false;
        });
    } catch (error) {
        console.error('Error initializing dashboard analytics:', error);
        initialization = Promise.resolve(false);
    }
    return initialization;
}

/** Preserve call order during startup without blocking the dashboard on analytics availability. */
function whenAnalyticsReady(action: () => void): void {
    void initAnalytics().then((ready) => {
        if (ready) action();
    }).catch((error) => {
        console.error('Error updating dashboard analytics:', error);
    });
}

/** Use the authenticated staff UID and separate accounts sharing the same browser. */
export function analyticsSetUser(userId: string | null): void {
    whenAnalyticsReady(() => {
        const nextUserId = userId || undefined;
        const previousUserId = amplitude.getUserId();
        if (previousUserId === nextUserId) return;
        if (previousUserId) amplitude.reset();
        if (nextUserId) amplitude.setUserId(nextUserId);
    });
}

/** Send explicit events with a snapshot of the identity, including during logout or account changes. */
export function analyticsLogEvent(eventName: string, properties: Record<string, unknown> = {}): void {
    whenAnalyticsReady(() => {
        // The SDK enriches events asynchronously, so preserve the user and device before a later reset.
        const identity = {
            user_id: amplitude.getUserId(),
            device_id: amplitude.getDeviceId(),
        };
        void amplitude.track(eventName, {
            ...properties,
            app: 'SuperPenguin dashboard',
            environment: process.env.NODE_ENV,
        }, identity).promise.then((result) => {
            // An accepted queue entry is not confirmation that Amplitude received the event.
            if (result.code !== 200) {
                console.warn('Dashboard analytics event was not delivered:', eventName, result.code, result.message);
            }
        }).catch((error) => {
            console.error('Error sending dashboard analytics event:', eventName, error);
        });
    });
}
